/**
 * The dev-only switcher that draws a module's board with invented plays.
 *
 * A leaderboard needs a hundred learners to have finished a course, which is not
 * something a person can produce by hand and not something a test can wait for —
 * so the three states the board has are reachable by *param*, and this is the
 * door: a banner that says the plays below are invented, and three chips that
 * switch between them. It is the same arrangement the book studio's preview banner
 * uses, for the same reason.
 *
 * Three rules kept deliberately:
 *
 *  - **It is never rendered unless `IS_DEV`**, and the param it sets is only read
 *    behind the same check on the screens that receive it — so a production build
 *    has no path to a fake board, and a shared URL cannot make one appear.
 *  - **It swaps the *data*, not the screens.** The invented plays go through the
 *    same aggregator and the same components the class's own plays do (see
 *    `#/lib/moduleFixtures`), so what the preview shows is what ships and cannot
 *    drift from it.
 *  - **It is loud.** A banner that looked like the app would end up in a
 *    screenshot presented as real; this one is captioned, and says out loud that
 *    the plays are invented.
 *
 * It has a second row it did not start with: **who the reader is pretending to be**
 * (see `#/lib/moduleFixtures`). The peer experience belongs to a *learner*, and a
 * preview browser is signed into one school account — so without naming a role
 * there is no way to look at the half of the feature that is about being behind
 * your classmates. Unlike the plays, this is a stored setting rather than a route
 * param, because the surface that most needs it is the card in the **feed**, which
 * has no route of its own.
 *
 * The plays are remembered too, for that same reason — a reader who picks *A full
 * board* here finds the same class in the timeline — and the two are stored
 * separately, because they are two questions: an invented board read by a teacher
 * is a state worth looking at.
 *
 * A third row says whether the reader *knows* anybody in the invented class. The
 * board can be invented; a follow cannot, because a follow is a relationship on a
 * profile rather than a fact about a play — so the one sentence that starts with a
 * friend's name is unreachable here without saying it out loud (see
 * `readModulePreviewFriend`).
 *
 * Removing the feature later is deleting this file, the `preview` param, the
 * fixture roster and the stored role — nothing else reads them.
 */
import {Pressable, StyleSheet, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {
  MODULE_PREVIEW_ROLES,
  MODULE_PREVIEW_STATES,
  type ModulePreviewRole,
  type ModulePreviewState,
} from '#/lib/moduleFixtures'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

export function ModulePreviewBar({
  state,
  onChange,
  role,
  onRoleChange,
  friend,
  onFriendChange,
  testIDPrefix = 'modulePreview',
}: {
  /** The state being shown; `null` means the class's real plays. */
  state: ModulePreviewState | null
  onChange: (next: ModulePreviewState | null) => void
  /** The reader being pretended to be; `off` means the real session. */
  role?: ModulePreviewRole
  onRoleChange?: (next: ModulePreviewRole) => void
  /**
   * Whether the leading played learner should count as somebody the reader follows.
   *
   * The invented class is not on anybody's follow list, so the one sentence that
   * leads with a friend's name is otherwise unreachable in a preview build (see
   * `#/lib/moduleFixtures`).
   */
  friend?: boolean
  onFriendChange?: (next: boolean) => void
  testIDPrefix?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID={`${testIDPrefix}Bar`}
      style={[a.mx_md, a.mb_md, a.p_sm, a.rounded_sm, a.gap_sm, styles.bar]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <View style={[styles.dot, {backgroundColor: t.palette.negative_500}]} />
        <Text style={[a.text_xs, a.font_bold, {color: t.palette.negative_500}]}>
          {l`PREVIEW — these plays are invented`}
        </Text>
      </View>
      <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
        <PreviewChip
          testID={`${testIDPrefix}Real`}
          label={l`The class's own`}
          selected={state === null}
          onPress={() => onChange(null)}
        />
        {MODULE_PREVIEW_STATES.map(option => (
          <PreviewChip
            key={option.id}
            testID={`${testIDPrefix}-${option.id}`}
            label={l(option.label)}
            selected={state === option.id}
            onPress={() => onChange(option.id)}
          />
        ))}
      </View>

      {/*
        Who the card thinks is reading it.

        The plays and the role are separate questions — an invented board read by
        a teacher is a real state to look at — so they are two rows, and the second
        one is only there when the caller has somewhere to put the answer.
      */}
      {onRoleChange ? (
        <View style={[a.gap_xs]}>
          <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
            {l`READING AS`}
          </Text>
          <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
            {MODULE_PREVIEW_ROLES.map(option => (
              <PreviewChip
                key={option.id}
                testID={`${testIDPrefix}Role-${option.id}`}
                label={l(option.label)}
                selected={(role ?? 'off') === option.id}
                onPress={() => onRoleChange(option.id)}
              />
            ))}
          </View>
        </View>
      ) : null}

      {/*
        Whether the reader knows anybody in the class.

        A third row, because it is a third question: an invented board read by a
        student who follows the leader is its own state — the one where the
        sentence starts with a name.
      */}
      {onFriendChange ? (
        <View style={[a.gap_xs]}>
          <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
            {l`A FRIEND IN THE CLASS`}
          </Text>
          <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
            <PreviewChip
              testID={`${testIDPrefix}Friend-off`}
              label={l`A stranger`}
              selected={!friend}
              onPress={() => onFriendChange(false)}
            />
            <PreviewChip
              testID={`${testIDPrefix}Friend-on`}
              label={l`Someone I follow`}
              selected={!!friend}
              onPress={() => onFriendChange(true)}
            />
          </View>
        </View>
      ) : null}
    </View>
  )
}

/**
 * One chip.
 *
 * Selected is a fill rather than a tick: three options that all look alike would
 * leave a reader guessing which board they are looking at, and the whole point of
 * this bar is that the difference between the states is legible.
 */
function PreviewChip({
  testID,
  label,
  selected,
  onPress,
}: {
  testID: string
  label: string
  selected: boolean
  onPress: () => void
}) {
  const t = useTheme()

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{selected}}
      onPress={onPress}
      style={[
        styles.chip,
        selected
          ? {backgroundColor: t.palette.primary_500}
          : t.atoms.bg_contrast_50,
      ]}>
      <Text
        style={[
          a.text_xs,
          a.font_semi_bold,
          selected ? styles.selectedInk : t.atoms.text,
        ]}>
        {label}
      </Text>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  bar: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(200, 60, 60, 0.5)',
    borderStyle: 'dashed',
  },
  dot: {width: 7, height: 7, borderRadius: 4},
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
  },
  selectedInk: {color: 'white'},
})
